
import React, { useState, useEffect } from 'react';
import PromptBuilder from './components/PromptBuilder';
import ImageCanvas from './components/ImageCanvas';
import EditorPanel from './components/EditorPanel';
import LandingPage from './components/LandingPage';
import ErrorBoundary from './components/ErrorBoundary';
import LogViewer, { LogEntry } from './components/LogViewer';
import { PromptConfig, GeneratedImage, Annotation, AppMode, ToolMode, SelectionRect } from './types';
import { MEDICAL_TAXONOMY, STYLES } from './constants';
import { generateMedicalImage, generateMedicalSeries, constructMedicalPrompt, editMedicalImage, generateMedicalVideo, checkAndRequestApiKey } from './services/geminiService';
import { Terminal } from 'lucide-react';

const DEFAULT_CONFIG: PromptConfig = {
  system: MEDICAL_TAXONOMY[0].name,
  region: MEDICAL_TAXONOMY[0].regions[0].name,
  focus: MEDICAL_TAXONOMY[0].regions[0].focusAreas[0],
  customModifier: '',
  plane: 'Coronal',
  view: 'Anterior',
  zoom: 'Gross Anatomy',
  style: STYLES[0].value,
  lighting: 'Studio Neutral (Textbook)',
  background: 'Pure White',
};

const AppContent: React.FC<{ addLog: (t: any, m: any) => void }> = ({ addLog }) => {
  const [config, setConfig] = useState<PromptConfig>(DEFAULT_CONFIG);
  const [appMode, setAppMode] = useState<AppMode>(AppMode.SINGLE);
  const [toolMode, setToolMode] = useState<ToolMode>(ToolMode.POINTER);
  const [outputFormat, setOutputFormat] = useState<'IMAGE' | 'VIDEO'>('IMAGE');
  
  // Image State
  const [generatedImage, setGeneratedImage] = useState<GeneratedImage | null>(null);
  const [seriesImages, setSeriesImages] = useState<GeneratedImage[]>([]);
  
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Annotation History State
  const [annotationHistory, setAnnotationHistory] = useState<Annotation[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const annotations = annotationHistory[historyIndex] || [];

  const setAnnotations = (action: React.SetStateAction<Annotation[]>) => {
    const newAnnotations = typeof action === 'function' 
      ? (action as (prev: Annotation[]) => Annotation[])(annotations)
      : action;
      
    setAnnotationHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newAnnotations);
      return newHistory;
    });
    setHistoryIndex(prev => prev + 1);
  };

  const undoAnnotations = () => {
    if (historyIndex > 0) setHistoryIndex(i => i - 1);
  };

  const redoAnnotations = () => {
    if (historyIndex < annotationHistory.length - 1) setHistoryIndex(i => i + 1);
  };

  const [focusedAnnotationId, setFocusedAnnotationId] = useState<string | null>(null);
  const [generatedPrompt, setGeneratedPrompt] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setAnnotationHistory([[]]); // Reset history
    setHistoryIndex(0);
    setFocusedAnnotationId(null);
    
    // Pass isVideo=true if outputFormat is VIDEO for correct prompt text
    const promptString = constructMedicalPrompt(config, outputFormat === 'VIDEO');
    setGeneratedPrompt(promptString);

    try {
      console.log(`Starting generation. Mode: ${appMode}, Format: ${outputFormat}`);
      
      if (outputFormat === 'VIDEO') {
        // Handle Video Generation (Veo)
        await checkAndRequestApiKey(); // Ensure Veo key requirement
        
        const videoUrl = await generateMedicalVideo(config);
        const newVid: GeneratedImage = {
          id: Date.now().toString(),
          url: videoUrl,
          prompt: promptString,
          timestamp: Date.now(),
          mediaType: 'VIDEO'
        };
        setGeneratedImage(newVid);
        setSeriesImages([]);

      } else {
        // Handle Image Generation
        if (appMode === AppMode.SINGLE) {
          const base64Image = await generateMedicalImage(config);
          const newImg: GeneratedImage = {
            id: Date.now().toString(),
            url: base64Image,
            prompt: promptString,
            timestamp: Date.now(),
            mediaType: 'IMAGE'
          };
          setGeneratedImage(newImg);
          setSeriesImages([]); 
        } else {
          const images = await generateMedicalSeries(config);
          setSeriesImages(images);
          setGeneratedImage(images[0]);
        }
      }
      console.log("Generation successful");
    } catch (err: any) {
      console.error("Generation Failed:", err);
      // Nice error message handling
      let msg = "Failed to generate content.";
      if (err.message?.includes("API key")) msg = "API Key Error: Please ensure a valid API key is configured in your environment.";
      else if (err.message?.includes("Video")) msg = "Video generation failed. Please try again or check usage limits.";
      else if (err.message) msg = `Error: ${err.message}`;
      
      setError(msg);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleInPaint = async (selection: SelectionRect, prompt: string) => {
    if (!generatedImage || generatedImage.mediaType === 'VIDEO') return;
    
    try {
      console.log("Starting In-Paint with prompt:", prompt);
      const newBase64 = await editMedicalImage(generatedImage.url, prompt, selection);
      const updatedImage = {
        ...generatedImage,
        url: newBase64,
        id: Date.now().toString(),
      };
      
      setGeneratedImage(updatedImage);
      
      if (seriesImages.length > 0) {
        setSeriesImages(prev => prev.map(img => img.id === generatedImage.id ? updatedImage : img));
      }
      console.log("In-Paint successful");
      
    } catch (err: any) {
      console.error("In-Paint Failed:", err);
      setError(`In-painting failed: ${err.message || 'Unknown error'}`);
    }
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-slate-950 text-slate-200">
      {/* Toast Error */}
      {error && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[100] bg-red-950/90 text-red-200 px-6 py-4 rounded-lg shadow-xl backdrop-blur-sm border border-red-500/50 animate-in fade-in slide-in-from-top-2 max-w-2xl text-center">
          <div className="flex items-center gap-2 mb-1 justify-center">
             <span className="font-bold text-red-400 uppercase tracking-wider text-xs">Generation Failed</span>
          </div>
          <p className="text-sm font-medium leading-relaxed">{error}</p>
          <button onClick={() => setError(null)} className="absolute top-2 right-2 text-red-400/60 hover:text-red-300">×</button>
        </div>
      )}

      <PromptBuilder 
        config={config} 
        setConfig={setConfig} 
        onGenerate={handleGenerate}
        isGenerating={isGenerating}
        appMode={appMode}
        setAppMode={setAppMode}
        outputFormat={outputFormat}
        setOutputFormat={setOutputFormat}
      />

      <ImageCanvas 
        image={generatedImage} 
        seriesImages={seriesImages}
        onSelectSeriesImage={setGeneratedImage}
        annotations={annotations} 
        setAnnotations={setAnnotations}
        focusedAnnotationId={focusedAnnotationId}
        isGenerating={isGenerating}
        toolMode={toolMode}
        setToolMode={setToolMode}
        onInPaint={handleInPaint}
      />

      <EditorPanel 
        annotations={annotations}
        setAnnotations={setAnnotations}
        generatedPrompt={generatedPrompt}
        generatedImage={generatedImage}
        onAnnotationFocus={setFocusedAnnotationId}
        onUndo={undoAnnotations}
        onRedo={redoAnnotations}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < annotationHistory.length - 1}
      />
    </div>
  );
};

const App: React.FC = () => {
  const [showLanding, setShowLanding] = useState(true);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [showLogs, setShowLogs] = useState(false);

  // Console Interception for Logs
  useEffect(() => {
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalLog = console.log;

    const addLogEntry = (type: 'info' | 'error' | 'warn', args: any[]) => {
      try {
        const message = args.map(a => {
          if (a instanceof Error) return a.toString();
          if (typeof a === 'object') return JSON.stringify(a);
          return String(a);
        }).join(' ');
        setLogs(prev => [...prev, { type, message, timestamp: Date.now() }]);
      } catch (e) {
        // Fallback for circular structures
        setLogs(prev => [...prev, { type, message: 'Object (Circular)', timestamp: Date.now() }]);
      }
    };

    console.error = (...args) => { addLogEntry('error', args); originalError.apply(console, args); };
    console.warn = (...args) => { addLogEntry('warn', args); originalWarn.apply(console, args); };
    console.log = (...args) => { addLogEntry('info', args); originalLog.apply(console, args); };

    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      console.log = originalLog;
    };
  }, []);

  const clearLogs = () => setLogs([]);

  return (
    <ErrorBoundary>
      {showLanding ? (
        <LandingPage onLaunch={() => setShowLanding(false)} />
      ) : (
        <>
          <AppContent addLog={() => {}} />
          
          {/* Floating Log Toggle */}
          <div className="fixed bottom-4 right-4 z-[90]">
            <button 
              onClick={() => setShowLogs(!showLogs)}
              className={`p-3 rounded-full shadow-xl transition-all border border-slate-700 ${
                showLogs ? 'bg-cyan-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
              title="Toggle System Logs"
            >
              <Terminal className="w-5 h-5" />
              {logs.filter(l => l.type === 'error').length > 0 && (
                 <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                 </span>
              )}
            </button>
          </div>

          <LogViewer 
            isOpen={showLogs} 
            onClose={() => setShowLogs(false)} 
            logs={logs} 
            onClear={clearLogs}
          />
        </>
      )}
    </ErrorBoundary>
  );
};

export default App;
